/* global google */
import store from '/@/store'
import { getRoot } from '/@/root'
import { ELocalStorageKey, EDeviceTypeName } from '/@/types'
import { getDeviceBySn } from '/@/api/manage'
import { message } from 'ant-design-vue'
import dockIcon from '/@/assets/icons/dock.png'
import rcIcon from '/@/assets/icons/rc.png'
import droneIcon from '/@/assets/icons/drone.png'

// Type definitions
interface DevicePosition {
  lat: number
  lng: number
  heading?: number
  timestamp: number
}

interface FlightPath {
  polyline: any
  positions: Array<{ lat: number; lng: number }>
  startPosition: { lat: number; lng: number } | null
}

interface FollowModeState {
  enabled: boolean
  targetSn: string | null
}

export function deviceTsaUpdate () {
  const root = getRoot()

  const icons = new Map([
    [EDeviceTypeName.Aircraft, droneIcon],
    [EDeviceTypeName.Gateway, rcIcon],
    [EDeviceTypeName.Dock, dockIcon],
  ])

  const markers = store.state.markerInfo.coverMap

  // Store for flight paths
  const flightPaths: Map<string, FlightPath> = new Map()

  // Store for device positions history
  const positionHistory: Map<string, DevicePosition[]> = new Map()

  // Store device types
  const deviceTypes: Map<string, number> = new Map()

  // Track if we have centered the map on devices
  let hasAutocentered = false

  // Follow mode state
  const followMode: FollowModeState = {
    enabled: false,
    targetSn: null,
  }

  // Configuration
  const config = {
    flightPathColor: '#FF6B00',
    flightPathWeight: 3,
    flightPathOpacity: 0.8,
    maxPathPoints: 1000,
    smoothAnimationDuration: 300,
    minDistanceForPathUpdate: 1, // meters
    minDistanceToStartPath: 3, // meters - drone must move this far to start drawing path
  }

  /**
   * Calculate distance between two points in meters
   */
  function calculateDistance (pos1: { lat: number; lng: number }, pos2: { lat: number; lng: number }): number {
    const R = 6371000
    const dLat = (pos2.lat - pos1.lat) * Math.PI / 180
    const dLng = (pos2.lng - pos1.lng) * Math.PI / 180
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(pos1.lat * Math.PI / 180) * Math.cos(pos2.lat * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  /**
   * Create a drone marker icon with rotation
   */
  function createDroneMarkerIcon (heading: number = 0): any {
    return {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 6,
      fillColor: '#2196F3',
      fillOpacity: 1,
      strokeColor: '#1565C0',
      strokeWeight: 2,
      rotation: heading,
      anchor: new google.maps.Point(0, 2.5),
    }
  }

  /**
   * Initialize a flight path polyline for a drone
   */
  function initFlightPath (sn: string, startPos: { lat: number; lng: number }): void {
    if (flightPaths.has(sn)) return

    const polyline = new google.maps.Polyline({
      path: [], // Start empty - only draw when drone moves
      strokeColor: config.flightPathColor,
      strokeOpacity: config.flightPathOpacity,
      strokeWeight: config.flightPathWeight,
      map: root.$map,
    })

    flightPaths.set(sn, {
      polyline,
      positions: [],
      startPosition: startPos, // Remember where drone started
    })

    console.log(`[TSA] Flight path initialized for ${sn} at`, startPos)
  }

  /**
   * Update flight path with new position
   */
  function updateFlightPath (sn: string, newPosition: { lat: number; lng: number }): void {
    const flightPath = flightPaths.get(sn)
    if (!flightPath) return

    // If path is empty, check if drone has moved enough from start to begin drawing
    if (flightPath.positions.length === 0) {
      if (flightPath.startPosition) {
        const distFromStart = calculateDistance(flightPath.startPosition, newPosition)
        if (distFromStart >= config.minDistanceToStartPath) {
          // Start the path from the original position
          flightPath.positions.push(flightPath.startPosition)
          flightPath.positions.push(newPosition)
          flightPath.polyline.setPath(flightPath.positions)
          console.log(`[TSA] Started drawing flight path for ${sn}, moved ${distFromStart.toFixed(1)}m`)
        }
      }
      return
    }

    // Path already has points - add new point if moved enough
    const lastPosition = flightPath.positions[flightPath.positions.length - 1]
    const distance = calculateDistance(lastPosition, newPosition)

    if (distance < config.minDistanceForPathUpdate) return

    flightPath.positions.push(newPosition)

    if (flightPath.positions.length > config.maxPathPoints) {
      flightPath.positions.shift()
    }

    flightPath.polyline.setPath(flightPath.positions)
  }

  /**
   * Remove flight path for a device
   */
  function removeFlightPath (sn: string): void {
    const flightPath = flightPaths.get(sn)
    if (flightPath) {
      flightPath.polyline.setMap(null)
      flightPaths.delete(sn)
    }
  }

  /**
   * Clear all flight paths
   */
  function clearAllFlightPaths (): void {
    flightPaths.forEach((flightPath) => {
      flightPath.polyline.setMap(null)
    })
    flightPaths.clear()
  }

  /**
   * Calculate heading from two positions
   */
  function calculateHeading (from: { lat: number; lng: number }, to: { lat: number; lng: number }): number {
    const dLng = (to.lng - from.lng) * Math.PI / 180
    const lat1 = from.lat * Math.PI / 180
    const lat2 = to.lat * Math.PI / 180

    const x = Math.sin(dLng) * Math.cos(lat2)
    const y = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng)

    let heading = Math.atan2(x, y) * 180 / Math.PI
    heading = (heading + 360) % 360

    return heading
  }

  /**
   * Smoothly animate marker to new position
   */
  function animateMarkerTo (
    marker: any,
    newPosition: { lat: number; lng: number },
    duration: number = config.smoothAnimationDuration
  ): void {
    const startPosition = marker.getPosition()
    if (!startPosition) {
      marker.setPosition(newPosition)
      return
    }

    const startLat = startPosition.lat()
    const startLng = startPosition.lng()
    const deltaLat = newPosition.lat - startLat
    const deltaLng = newPosition.lng - startLng

    const startTime = performance.now()

    function animate (currentTime: number) {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const easeProgress = 1 - Math.pow(1 - progress, 3)

      const currentLat = startLat + deltaLat * easeProgress
      const currentLng = startLng + deltaLng * easeProgress

      marker.setPosition({ lat: currentLat, lng: currentLng })

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }

  /**
   * Center map on all devices
   */
  function centerMapOnAllDevices (): void {
    if (!root.$map) return

    const bounds = new google.maps.LatLngBounds()
    let hasDevices = false

    Object.keys(markers).forEach(sn => {
      const marker = markers[sn]
      if (marker) {
        const position = marker.getPosition()
        if (position) {
          bounds.extend(position)
          hasDevices = true
        }
      }
    })

    if (hasDevices) {
      console.log('[TSA] Centering map on devices')
      root.$map.fitBounds(bounds, { padding: 100 })
      google.maps.event.addListenerOnce(root.$map, 'idle', () => {
        const zoom = root.$map.getZoom()
        if (zoom > 18) {
          root.$map.setZoom(18)
        } else if (zoom < 12) {
          root.$map.setZoom(12)
        }
      })
    }
  }

  /**
   * Initialize a marker on the map
   */
  function initMarker (type: number, name: string, sn: string, lng?: number, lat?: number, heading?: number) {
    console.log(`[TSA] initMarker called: sn=${sn}, type=${type}, name=${name}, lat=${lat}, lng=${lng}`)

    if (markers[sn]) {
      console.log(`[TSA] Marker already exists for ${sn}`)
      return
    }

    if (!root.$map) {
      console.error('[TSA] Map not initialized')
      return
    }

    // Use provided coordinates or a default
    const position = {
      lat: lat !== undefined ? lat : 9.0820,
      lng: lng !== undefined ? lng : 8.6753
    }

    console.log('[TSA] Creating marker at position:', position)

    deviceTypes.set(sn, type)

    if (type === EDeviceTypeName.Aircraft) {
      markers[sn] = new google.maps.Marker({
        position,
        map: root.$map,
        title: name,
        icon: createDroneMarkerIcon(heading || 0),
        zIndex: 100,
      })

      // Initialize flight path
      initFlightPath(sn, position)
    } else {
      markers[sn] = new google.maps.Marker({
        position,
        map: root.$map,
        title: name,
        icon: {
          url: icons.get(type),
          scaledSize: new google.maps.Size(40, 40),
          anchor: new google.maps.Point(20, 20),
        },
        zIndex: type === EDeviceTypeName.Gateway ? 50 : 10,
      })
    }

    console.log(`[TSA] Marker created for ${sn}`)

    positionHistory.set(sn, [{
      lat: position.lat,
      lng: position.lng,
      heading: heading || 0,
      timestamp: Date.now(),
    }])

    // Auto-center map when first device appears
    if (!hasAutocentered) {
      hasAutocentered = true
      setTimeout(() => {
        centerMapOnAllDevices()
      }, 1000)
    }
  }

  /**
   * Remove a marker from the map
   */
  function removeMarker (sn: string) {
    if (!markers[sn]) return
    console.log(`[TSA] Removing marker for ${sn}`)
    markers[sn].setMap(null)
    delete markers[sn]

    removeFlightPath(sn)
    positionHistory.delete(sn)
    deviceTypes.delete(sn)
  }

  /**
   * Add a marker by fetching device info
   */
  function addMarker (sn: string, lng?: number, lat?: number, heading?: number) {
    console.log(`[TSA] addMarker called: sn=${sn}, lat=${lat}, lng=${lng}`)

    const workspaceId = localStorage.getItem(ELocalStorageKey.WorkspaceId)!
    getDeviceBySn(workspaceId, sn)
      .then(data => {
        if (data.code !== 0) {
          console.error(`[TSA] Failed to get device info for ${sn}:`, data.message)
          message.error(data.message)
          return
        }
        const dev = data.data
        console.log('[TSA] Got device info:', dev)
        initMarker(dev.domain, dev.nickname, sn, lng, lat, heading)
      })
      .catch(err => {
        console.error('[TSA] Failed to fetch device:', err)
      })
  }

  /**
   * Move a marker to new coordinates with smooth animation
   */
  function moveTo (sn: string, lng: number, lat: number, heading?: number) {
    const newPosition = { lat, lng }

    if (!markers[sn]) {
      console.log(`[TSA] moveTo: Marker not found for ${sn}, creating...`)
      addMarker(sn, lng, lat, heading)
      return
    }

    const marker = markers[sn]
    const deviceType = deviceTypes.get(sn)

    const history = positionHistory.get(sn)
    let calculatedHeading = heading

    if (history && history.length > 0 && deviceType === EDeviceTypeName.Aircraft) {
      const lastPos = history[history.length - 1]

      if (calculatedHeading === undefined) {
        const distance = calculateDistance(
          { lat: lastPos.lat, lng: lastPos.lng },
          newPosition
        )
        if (distance > 1) {
          calculatedHeading = calculateHeading(
            { lat: lastPos.lat, lng: lastPos.lng },
            newPosition
          )
        } else {
          calculatedHeading = lastPos.heading || 0
        }
      }

      marker.setIcon(createDroneMarkerIcon(calculatedHeading))
      updateFlightPath(sn, newPosition)
    }

    if (history) {
      history.push({
        lat,
        lng,
        heading: calculatedHeading,
        timestamp: Date.now(),
      })
      if (history.length > 100) {
        history.shift()
      }
    }

    animateMarkerTo(marker, newPosition)

    if (followMode.enabled && followMode.targetSn === sn) {
      smoothPanTo(newPosition)
    }
  }

  /**
   * Smooth pan to position
   */
  function smoothPanTo (position: { lat: number; lng: number }): void {
    if (!root.$map) return
    root.$map.panTo(position)
  }

  /**
   * Enable follow mode for a specific drone
   */
  function enableFollowMode (sn: string): void {
    followMode.enabled = true
    followMode.targetSn = sn

    const marker = markers[sn]
    if (marker) {
      const position = marker.getPosition()
      if (position) {
        root.$map.panTo(position)
        root.$map.setZoom(18)
      }
    }
  }

  /**
   * Disable follow mode
   */
  function disableFollowMode (): void {
    followMode.enabled = false
    followMode.targetSn = null
  }

  /**
   * Toggle follow mode for a drone
   */
  function toggleFollowMode (sn: string): boolean {
    if (followMode.enabled && followMode.targetSn === sn) {
      disableFollowMode()
      return false
    } else {
      enableFollowMode(sn)
      return true
    }
  }

  /**
   * Get current follow mode state
   */
  function getFollowModeState (): FollowModeState {
    return { ...followMode }
  }

  /**
   * Show/hide flight path for a specific drone
   */
  function setFlightPathVisibility (sn: string, visible: boolean): void {
    const flightPath = flightPaths.get(sn)
    if (flightPath) {
      flightPath.polyline.setVisible(visible)
    }
  }

  /**
   * Clear flight path for a specific drone
   */
  function clearFlightPath (sn: string): void {
    const flightPath = flightPaths.get(sn)
    if (flightPath) {
      flightPath.positions = []
      flightPath.polyline.setPath([])
      // Reset start position to current marker position
      const marker = markers[sn]
      if (marker) {
        const pos = marker.getPosition()
        if (pos) {
          flightPath.startPosition = { lat: pos.lat(), lng: pos.lng() }
        }
      }
    }
  }

  /**
   * Set flight path color for a specific drone
   */
  function setFlightPathColor (sn: string, color: string): void {
    const flightPath = flightPaths.get(sn)
    if (flightPath) {
      flightPath.polyline.setOptions({ strokeColor: color })
    }
  }

  /**
   * Get all flight paths
   */
  function getAllFlightPaths (): Map<string, FlightPath> {
    return new Map(flightPaths)
  }

  /**
   * Focus on a specific device without enabling follow mode
   */
  function focusOnDevice (sn: string): void {
    const marker = markers[sn]
    if (marker && root.$map) {
      const position = marker.getPosition()
      if (position) {
        root.$map.panTo(position)
        root.$map.setZoom(18)
      }
    }
  }

  /**
   * Reset autocenter flag
   */
  function resetAutocenter (): void {
    hasAutocentered = false
  }

  return {
    marker: markers,
    initMarker,
    removeMarker,
    moveTo,
    addMarker,
    centerMapOnAllDevices,
    enableFollowMode,
    disableFollowMode,
    toggleFollowMode,
    getFollowModeState,
    setFlightPathVisibility,
    clearFlightPath,
    setFlightPathColor,
    clearAllFlightPaths,
    getAllFlightPaths,
    focusOnDevice,
    removeFlightPath,
    resetAutocenter,
  }
}
